import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { GamesGroupService, GroupType, IAnswer, ProcessedPredictorAnswer, UnprocessedPredictorAnswer } from '@fgb/core';
import { firstValueFrom, tap } from 'rxjs';

type SlidePosition = 'center' | 'left' | 'right' | 'hidden-left' | 'hidden-right';

@Component({
  selector: 'fgbcl-answer-type-video-carousel',
  templateUrl: './answer-type-video-carousel.component.html',
  styleUrl: './answer-type-video-carousel.component.scss',
  animations: [
    trigger('slideContent', [
      state('visible', style({ height: '*', opacity: 1, pointerEvents: 'auto' })),
      state('hidden', style({ height: '0', padding: '0', opacity: 0.6, pointerEvents: 'none' })),
      transition('visible <=> hidden', animate('350ms cubic-bezier(0.4, 0, 0.2, 1)')),
    ]),
  ],
})
export class AnswerTypeVideoCarouselComponent implements OnInit {
  selectedAnswerId = signal<number | null>(null);
  currentIndex = 0;
  completed = false;
  loading = false;
  correctAnswerId: number;
  selectedAnswerIsCorrect: boolean;

  @Input() answers: IAnswer[] | undefined;
  @Input() questionId: number;
  @Input() groupId: number;
  @Input() groupType: GroupType;

  @Output() answerSelected = new EventEmitter<any>();
  @Output() loadingState = new EventEmitter<boolean>();

  constructor(private gamesGroupService: GamesGroupService) {}

  ngOnInit() {
    if (this.groupType === GroupType.Predictor) {
      const selectedAnswer = this.answers
        ?.map((a) => a as ProcessedPredictorAnswer | UnprocessedPredictorAnswer)
        .find((a) => a.UserSelected);

      if (selectedAnswer) {
        this.selectedAnswerId.set(selectedAnswer.Id);

        this.currentIndex = this.answers?.findIndex((a) => a.Id === selectedAnswer.Id) || 0;
      }
    }
  }

  onAnswerSelected(answer: IAnswer) {
    if (this.groupType == GroupType.Trivia) {
      this.submitTrivia(answer);
      return;
    }

    this.selectedAnswerId.set(answer.Id);
    this.answerSelected.emit(answer);
  }

  private submitTrivia(answer: IAnswer) {
    if (this.completed) {
      return;
    }

    this.loading = true;
    this.loadingState.emit(this.loading);
    this.selectedAnswerId.set(answer.Id);

    firstValueFrom(
      this.gamesGroupService.submitTriviaAnswer(this.questionId, this.selectedAnswerId()!, this.groupId).pipe(
        tap((result) => {
          this.correctAnswerId = result.CorrectAnswerId;
          this.selectedAnswerIsCorrect = this.correctAnswerId === this.selectedAnswerId();
        })
      )
    ).then(() => {
      firstValueFrom(this.gamesGroupService.fetchTriviaGroupGames(this.groupId)).then((gameDetails) => {
        setTimeout(() => {
          this.answers = gameDetails.Questions.find((question) => question.Id == this.questionId)?.Answers;
          this.loading = false;
          this.completed = true;
          this.answerSelected.emit(gameDetails);
          this.loadingState.emit(this.loading);
        }, 300);
      });
    });
  }

  // Carousel navigation methods
  goToNext(): void {
    if (this.answers && this.currentIndex < this.answers.length - 1) {
      this.currentIndex++;
    }
  }

  goToPrev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  goToSlide(index: number): void {
    this.currentIndex = index;
  }

  getSlidePosition(index: number): SlidePosition {
    const diff = index - this.currentIndex;

    if (diff === 0) {
      return 'center';
    } else if (diff === 1) {
      return 'right';
    } else if (diff === -1) {
      return 'left';
    } else if (diff > 1) {
      return 'hidden-right';
    } else {
      return 'hidden-left';
    }
  }

  isCenterSlide(index: number): boolean {
    return this.getSlidePosition(index) === 'center';
  }

  isFirstSlide(): boolean {
    return this.currentIndex === 0;
  }

  isLastSlide(): boolean {
    return !this.answers || this.currentIndex === this.answers.length - 1;
  }
}
